import { ColumnApi, GridApi } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import React, { createRef, forwardRef } from 'react';
import { BasicGrid, IBasicGridRefs } from '../BasicGrid/BasicGrid';
import { Footer, IFooterComponentProps } from './Footer';
import { Pagination } from './Pagination';
import { Container } from './ServerPagedGrid.styles';
import { buildColumnDefinitions } from './utils';

export interface IServerPagedGridProps extends AgGridReactProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMoreDataRequest: (skip: number, limit: number) => Promise<any>;
    handleColumnInteraction?: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialData?: Array<any>;
    stickyTop: string;
    footerComponent?: React.FC<IFooterComponentProps>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    footerComponentProps?: any;
}

export interface IServerPagedGridRefs {
    gridApi: () => GridApi | undefined;
    columnApi: () => ColumnApi | undefined;
    autoWidth: () => void;
}

export const ServerPagedGrid = forwardRef<IServerPagedGridRefs, IServerPagedGridProps>(
    ({ footerComponentProps, footerComponent, handleColumnInteraction, columnDefs, domLayout = 'autoHeight', ...props }, ref) => {
        const gridRef = createRef<IBasicGridRefs>();

        const builtColumnDefs = columnDefs ? buildColumnDefinitions(columnDefs?.filter((e) => e)) : [];

        React.useImperativeHandle(ref, () => ({
            gridApi: () => gridRef.current?.gridApi(),
            columnApi: () => gridRef.current?.columnApi(),
            autoWidth: () => gridRef.current?.autoWidth(),
        }));

        const handlePaginationClick = (skip: number, offset: number) => {
            gridRef.current?.gridApi()?.showLoadingOverlay();

            props.onMoreDataRequest(skip, offset).then(() => {
                gridRef.current?.gridApi()?.hideOverlay();
            });
        };

        const handleUserColumnInteraction = () => {
            if (handleColumnInteraction) {
                handleColumnInteraction();
            }

            if (domLayout === 'autoHeight') {
                gridRef?.current?.autoWidth();
            }
        };

        const handleOnGridReady = () => {
            if (domLayout === 'autoHeight') {
                gridRef?.current?.autoWidth();
            }
        };

        return (
            <>
                <Container stickyTop={props.stickyTop} hasResults>
                    <BasicGrid
                        headerHeight={32}
                        rowHeight={40}
                        onGridHasLoaded={handleOnGridReady}
                        onUserColumnInteraction={() => handleUserColumnInteraction()}
                        domLayout={domLayout}
                        columnDefs={builtColumnDefs}
                        ref={gridRef}
                        {...props}
                    />
                </Container>
                {footerComponent && <Footer Component={footerComponent} componentProps={footerComponentProps} />}
                {props?.rowData?.length && <Pagination hasDropShadow pageRecordCount={props?.rowData?.length} onClick={handlePaginationClick} />}
            </>
        );
    }
);
